import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLanguage } from "@/i18n";
import { fileUrl, isVideo } from "@/api";

const Media = ({ src, alt, className }) =>
  isVideo(src) ? (
    <video src={fileUrl(src)} autoPlay muted loop playsInline preload="metadata" className={className} />
  ) : (
    <img src={fileUrl(src, 800)} alt={alt} loading="lazy" decoding="async" className={className} />
  );

export const Portfolio = ({ items, artists }) => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const filtered = filter === "all" ? items : items.filter((i) => i.artist_id === filter);

  return (
    <section id="portfolio" className="px-4 sm:px-8 lg:px-16 py-20 sm:py-28 border-t border-white/10" data-testid="portfolio-gallery">
      <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-3">02 —</p>
      <h2 className="font-display uppercase text-5xl sm:text-7xl tracking-wide">{t("portfolio_title")}</h2>
      <p className="text-zinc-400 mt-3 max-w-xl">{t("portfolio_subtitle")}</p>

      <div className="flex flex-wrap gap-2 mt-10" data-testid="portfolio-filters">
        <button onClick={() => setFilter("all")} data-testid="filter-all"
          className={`font-mono2 text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors duration-300 ${filter === "all" ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
          {t("portfolio_filter_all")}
        </button>
        {artists.map((a) => (
          <button key={a.id} onClick={() => setFilter(a.id)} data-testid={`filter-artist-${a.id}`}
            className={`font-mono2 text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors duration-300 ${filter === a.id ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
            {a.name}
          </button>
        ))}
      </div>

      <div className="columns-2 md:columns-3 lg:columns-4 gap-4 mt-10 [&>button]:mb-4">
        {filtered.map((item, i) => (
          <motion.button key={item.id} onClick={() => setLightbox(item)} data-testid={`portfolio-item-${item.id}`}
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: (i % 4) * 0.06 }}
            className="group block w-full overflow-hidden break-inside-avoid relative">
            <Media src={item.image} alt={item.title} className="img-bw w-full object-cover group-hover:scale-105" />
            {item.title && (
              <span className="absolute bottom-0 left-0 right-0 p-3 text-left font-mono2 text-[10px] tracking-[0.2em] uppercase bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                {item.title}
              </span>
            )}
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/90 backdrop-blur flex items-center justify-center p-6"
            onClick={() => setLightbox(null)} data-testid="portfolio-lightbox">
            {isVideo(lightbox.image) ? (
              <motion.video src={fileUrl(lightbox.image)} autoPlay muted loop playsInline controls
                initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="max-h-[85vh] max-w-full" />
            ) : (
              <motion.img src={fileUrl(lightbox.image)} alt={lightbox.title}
                initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="max-h-[85vh] max-w-full object-contain" />
            )}
            <button onClick={() => setLightbox(null)} data-testid="lightbox-close" aria-label={t("close")}
              className="absolute top-6 right-6 border border-white/20 p-2 hover:bg-white hover:text-black transition-colors">
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
